import { ReactElement, ReactNode } from 'react';
import { PartialDeep } from 'type-fest';
import type { ContainerBlock } from '~/models/blocks/container-block.model';
import type { TextBlock } from '~/models/blocks/text-block.model';
import type { ImageBlock } from '~/models/blocks/image-block.model';
import type { Flow } from '~/models/flow.model';
import {
    CompositionType,
    IChildLayout,
    IImage,
    IService,
    IServiceTemplate,
    RevisionType,
    ServicesItemsLayoutType,
    TBlockType,
    TInputTypeValidation
} from '~/types/model.types';
import type { QuestionBlock } from '~/models/blocks/question-block.model';
import type { Page } from '~/models/page.model';
import type { VideoBlock } from '~/models/blocks/video-block.model';
import type { SpacerBlock } from '~/models/blocks/spacer-block.model';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import type { ContractBlock } from '~/models/blocks/contract-block.model';
import type { ContractHTMLBlock } from '~/models/blocks/contract-html-block.model';
import type { SignaturesBlock } from '~/models/blocks/signatures-block.model';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import type { ServicesBlock } from '~/models/blocks/services-block.model';
import type { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import type { InvoiceServiceItemBlock } from '~/models/blocks/invoice-service-item-block.model';
import type { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import type { PaymentScheduleBlock } from '~/models/blocks/payment-schedule-block.model';
import type { PaymentBlock } from '~/models/blocks/payment-block.model';
import type { ContainerableBlock } from '~/models/blocks/containerable-block.model';
import type { InvoiceHeaderBlock } from '~/models/blocks/invoice-header-block.model';
import type { SignatureBlock } from '~/models/blocks/signature-block.model';
import type { Variable } from '~/models/variable.model';
import type { SocialBlock } from '~/models/blocks/social-block.model';
import {
    IServiceResponse,
    ITransformedPageResponse,
    IVariableResponse,
    TBlockResponse,
    TESServiceOrPackage
} from '~/types/server.types';
import { ICompositionLayoutStruct } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.types';
import type { InvoiceSummaryBlock } from '~/models/blocks/invoice-summary-block.model';
import type { ChargeContainerBlock } from '~/models/blocks/charge-container-block.model';
import type { ChargeBlock } from '~/models/blocks/charge-block.model';
import type { InvoiceColumnHeaderBlock } from '~/models/blocks/invoice-column-header-block.model';
import type { ServiceSelectionBlock } from '~/models/blocks/service-selection-block.model';
import { PaymentScheduleBlockErrors } from '~/types/error.types';
import { IModal } from '@honeybook/hbui/dist/components/HBModal/HBModal';
import type { ScheduleSessionBlock } from '~/models/blocks/schedule-session-block.model';
import type { ScheduleContainerBlock } from '~/models/blocks/schedule-container-block.model';
import type { IframeBlock } from '~/models/blocks/iframe-block.model';

export type TBlock =
    | ChargeBlock
    | ChargeContainerBlock
    | ContainerBlock
    | ContainerableBlock
    | ContractBlock
    | ContractHTMLBlock
    | IframeBlock
    | ImageBlock
    | InvoiceBlock
    | InvoiceColumnHeaderBlock
    | InvoiceHeaderBlock
    | InvoiceServiceItemBlock
    | InvoiceSummaryBlock
    | PaymentBlock
    | PaymentScheduleBlock
    | QuestionBlock
    | ScheduleContainerBlock
    | ScheduleSessionBlock
    | ServiceItemBlock
    | ServiceSelectionBlock
    | ServiceSelectionItemBlock
    | ServicesBlock
    | SignatureBlock
    | SignaturesBlock
    | SocialBlock
    | SpacerBlock
    | TextBlock
    | VideoBlock;

export type TContainerableBlock =
    | ChargeContainerBlock
    | ContainerBlock
    | ContractBlock
    | InvoiceBlock
    | InvoiceServiceItemBlock
    | ScheduleContainerBlock
    | ServiceItemBlock
    | ServiceSelectionBlock
    | ServiceSelectionItemBlock
    | ServicesBlock
    | SignaturesBlock;

export type TBlockDataForServer = ReturnType<TBlock['getDataForServer']>;

export const CONTENT_BLOCK_TYPES: TBlockType[] = [
    'text_block',
    'image_block',
    'spacer_block',
    'social_block',
    'video_block',
    'question_block',
    'iframe_block'
];

export type TCompositionContentTypesWithoutCompanyInfo = Exclude<
    CompositionContentType,
    CompositionContentType.CompanyInfo
>;

export enum CompositionContentType {
    Composition = 'composition',
    Question = 'question',
    Image = 'image',
    CanvaImage = 'canva image',
    Text = 'text',
    Video = 'video',
    CompanyInfo = 'company info'
}

export const CompositionContentTypeToBlockType: Record<
    CompositionContentType,
    TBlockType
> = {
    [CompositionContentType.Composition]: 'container_block',
    [CompositionContentType.Question]: 'question_block',
    [CompositionContentType.Image]: 'image_block',
    [CompositionContentType.CanvaImage]: 'image_block',
    [CompositionContentType.Text]: 'text_block',
    [CompositionContentType.Video]: 'video_block',
    [CompositionContentType.CompanyInfo]: 'company_info_block'
};

export enum CompositionContentTypeByBlockType {
    Question = 'question_block',
    Image = 'image_block',
    Text = 'text_block',
    Video = 'video_block',
    Composition = 'container_block'
}

export type TBlockWithVariables = SignatureBlock | PaymentBlock; // | QuestionBlock;

export type TBlockCreationDirection = 'top' | 'bottom';

export enum CommandType {
    Create = 'CREATE',
    Update = 'UPDATE',
    Delete = 'DELETE'
}

export interface IBaseCommand {
    id: string;
    type: CommandType;
}

export interface ICreateCommand extends IBaseCommand {
    data: TBlock | Page;
}

export enum DataHandleMethod {
    merge = 'merge',
    replace = 'replace'
}

export interface IUpdateCommand extends IBaseCommand {
    data:
        | Partial<TBlockResponse>
        | Partial<ITransformedPageResponse>
        | PartialDeep<Flow>
        | Partial<IVariableResponse>;
    _id?: string;
    dataHandleMethod?: DataHandleMethod;
}

export interface IDeleteCommand extends IBaseCommand {
    data: {
        block_id?: string;
        page_id?: string;
    };
}

export type TCommand = ICreateCommand | IUpdateCommand | IDeleteCommand;

export interface IBaseContext {
    commands?: TCommand[];
    revertCommands?: TCommand[];
}

export interface IBaseFlowContext extends IBaseContext {
    flow: Flow;
    pageId?: string;
}

export interface IBlockBaseContext extends IBaseFlowContext {
    blockId: string;
}

export interface ICreateBlockContext extends IBlockBaseContext {
    blockType?: TBlockType;
    order: number;
    compositionType?: CompositionType;
    newTargetBlock?: TBlock;
    isCompositionLayout?: boolean;
    oldSelectedBlockId?: string;
    parentOldChildrenLayout?: IChildLayout[];
    variables?: IVariableResponse[];
    initialAttributes?: any;
    compositionContentType?: CompositionContentType;
}

export interface IAttributionContext extends IBaseFlowContext {
    source_flow_id: string;
    source_page_id?: string;
    source_block_id?: string;
}

export interface ICreateInvoiceBlockContext
    extends ICreateBlockContext,
        ICreatePageContext {
    onlyChargeBlock?: boolean;
}

export interface IReplaceBlockContext
    extends ICreateBlockContext,
        IDuplicateBlockContext,
        IAttributionContext {
    templateBlock: TBlock;
    parent: ContainerableBlock;
    templateId: string;
}

export interface IReplaceBlockFromClipContext
    extends ICreateBlockContext,
        IAttributionContext {
    parent: ContainerableBlock;
    clipboardBlock: TBlock;
    allRelevantBlocks: TBlock[];
    originalBlockToReplace: TBlock;
}

export interface ICreateCompositionBlockContext extends ICreateBlockContext {
    layout?: ICompositionLayoutStruct;
}

export enum CreateCompositionBlockStages {
    createDefaultBlock = 'createDefaultBlock',
    deleteCurrentLayout = 'deleteCurrentLayout',
    createCompositionLayout = 'createCompositionLayout'
}

export enum ReplaceBlockWithTemplateStages {
    deleteExistingBlock = 'deleteExistingBlock',
    copyTemplateBlock = 'copyTemplateBlock'
}

export enum ReplaceBlockWithBlockStages {
    deleteExistingBlock = 'deleteExistingBlock',
    createBlock = 'createBlock'
}

export interface ICreateQuestionBlockContext
    extends ICreateBlockContext,
        IUpdateBlockContext {
    type?: QuestionType;
}

export enum CreateQuestionBlockStages {
    createDefaultBlock = 'createDefaultBlock',
    updateQuestionType = 'updateQuestionType'
}

export interface IUpdateBlockContext extends IBlockBaseContext {
    data?: object;
    oldData?: object;
    updateDataHandleMethod?: DataHandleMethod;
}

export interface IBlockMovedContext {
    movedChildrenNewParents?: { [childId: string]: string };
    movedChildrenOldParents?: { [childId: string]: string };
}

export interface IBulkUpdateBlockContext extends IBlockBaseContext {
    data: Array<Partial<TBlock>>;
    oldData?: Array<Partial<TBlock>>;
}

export interface IUpdateBlockColumnsContext
    extends IBlockBaseContext,
        IBlockMovedContext {
    nColumns: number;
    oldMovedChildrenParents?: Record<string, string>;
    createdVerticalContainers?: Array<ContainerBlock>;
}

export interface IUpdateServiceSelectionLayoutContext
    extends IBlockBaseContext,
        IBlockMovedContext {
    newLayout: ServicesItemsLayoutType;
    oldMovedChildrenParents?: Record<string, string>;
}

export interface IMoveBlockContext
    extends IBlockBaseContext,
        IBlockMovedContext {
    order: number;
    oldOrder: number;
}

export interface IDuplicateBlockContext extends IBlockBaseContext {
    duplicate_block?: TBlock;
}

export interface IAttributedDupliacteBlockCtx
    extends IDuplicateBlockContext,
        IAttributionContext {}

export interface ICreatePackageBlockContext extends IBlockBaseContext {
    block?: TBlock;
    services?: IServiceResponse[];
    item?: TESServiceOrPackage;
    updatedServiceItem?: IService;
    scope: ServiceItemScope;
    serviceImage: IImage;
}

export interface ICreatePackageBlockFromTemplateContext
    extends IBlockBaseContext {
    block: TBlock;
    item: IServiceTemplate;
    scope: ServiceItemScope;
    updatedServiceItem?: IService;
    serviceImage?: IImage;
}

export interface ICreatePageContext extends IBaseFlowContext {
    newPageId?: string;
    oldSelectedPageId?: string;
}

export interface IDuplicatePageContext
    extends ICreatePageContext,
        IAttributionContext {
    page: Page;
    order?: number;
    isImporting?: boolean;
}

export interface IDuplicatePageOptions {
    order?: number;
    isImporting?: boolean;
    withAttributions?: boolean;
}

export interface IUpdatePageContext extends IBaseFlowContext {
    data: Partial<Page>;
    oldData?: Partial<Page>;
}

export interface IDeletePageContext extends IBaseFlowContext {
    rootBlockId: string;
}

export interface IUpdateVariableContext extends IBaseFlowContext {
    data: Partial<Variable>;
    variableId: string;
}

export enum BlockDndDirection {
    Top = 'top',
    Bottom = 'bottom'
}

export enum DnDTypes {
    Block = 'block',
    HorizontalBlock = 'horizontal-block',
    ServiceItem = 'service-item',
    ServiceSelectionItem = 'service-selection-item',
    Package = 'package',
    ServiceItemInPackage = 'service-in-package'
}

export interface IDraggableItem {
    id: string;
    index: number;
    type: DnDTypes;
}

export interface IPrintOrder {
    blockIds: string[];
    breakAfterBlockIds?: string[];
    revision: RevisionType;
}

export type TCompositionLayoutType =
    | CompositionLayoutType
    | CompanyInfoLayoutType;

export enum CompositionLayoutType {
    OneVideo = 'OneVideo',
    OneImage = 'OneImage',
    TextOnly = 'TextOnly',
    OneQuestion = 'OneQuestion',
    TitleAndText = 'TitleAndText',
    IconAndText = 'IconAndText',
    TwoColumnText = 'TwoColumnText',
    TwoImages = 'TwoImages',
    TextAndVideo = 'TextAndVideo',
    ImageAndQuestions = 'ImageAndQuestions',
    ImagesAndTextGrid = 'ImageAndTextGrid',
    Splash = 'Splash',
    VideoAndText = 'VideoAndText',
    ImageAndText = 'ImageAndText',
    TextAndQuestions = 'TextAndQuestions',
    ImageGrid = 'ImageGrid',
    LeftImageAndText = 'LeftImageAndText',
    ImagesAndText = 'ImagesAndText',
    Questionnaire = 'Questionnaire',
    Form = 'Form',
    TextApproval = 'TextApproval',
    ImageApproval = 'ImageApproval',
    ImageSelection = 'ImageSelection',
    UploadQuestion = 'UploadQuestion'
}

export enum CompanyInfoLayoutType {
    LogoOnLeft = 'logo_on_left',
    Centered = 'centered',
    LogoOnRight = 'logo_on_right',
    Hero = 'hero',
    StackedLeft = 'stacked_left',
    Footer = 'footer',
    FooterCentered = 'footer_centered',
    LogoOnly = 'logo_only',
    Header = 'header'
}

// Question type names correspond to the backend QUESTION_TYPES enum
export enum QuestionType {
    ShortText = 'open_question',
    LongText = 'textarea',
    Dropdown = 'dropdown',
    SingleChoice = 'radio',
    MultiChoice = 'checkbox',
    Date = 'date_picker',
    Number = 'number',
    URL = 'url',
    Upload = 'upload_attachments'
}

export enum FixedProjectLinkedQuestionType {
    Address = 'event_location',
    LeadSource = 'lead_source',
    ProjectType = 'event_type',
    ProjectDate = 'event_date',
    AboutProject = 'event_details',
    Budget = 'event_budget',
    Guests = 'event_guests'
}

export enum FixedContactLinkedQuestionType {
    PhoneNumber = 'phone_number',
    // TODO add back when CORE-13007 [linked job title in a question block doesn't show up in contact details] is fixed
    // JobTitle = 'job_title',
    MailingAddress = 'address',
    WebsiteUrl = 'website_url'
}

export type FixedLinkedQuestionType =
    | FixedProjectLinkedQuestionType
    | FixedContactLinkedQuestionType;

export enum CompanySettingsConnectedQuestions {
    lead_source = 'lead sources',
    event_type = 'project types'
}

export enum CompanySettingsAnalyticsEvents {
    lead_source = AnalyticsEvents.EditCompanyLeadSources,
    event_type = AnalyticsEvents.EditCompanyProjectTypes
}

export interface IQuestion {
    _id?: string;
    _type?: string;
    data_key?: string;
    order?: number;
    is_deletable?: boolean;
    is_required?: boolean;
    text?: string;
    question_type: QuestionType;
    connected_property?: FixedLinkedQuestionType | '';
    placeholder?: string;
    validation_type?: TInputTypeValidation;
}

export interface ITextQuestion extends IQuestion {
    text: string;
    placeholder?: string;
    question_type:
        | QuestionType.LongText
        | QuestionType.ShortText
        | QuestionType.URL
        | QuestionType.Number;
}

export interface IUploadQuestion extends IQuestion {
    text: string;
    question_type: QuestionType.Upload;
}

export interface IDateQuestion extends IQuestion {
    placeholder: string;
    start_date_text: string;
    end_date_text: string;
    start_time_text: string;
    end_time_text: string;
    has_end_date: boolean;
    has_time: boolean;
    question_type: QuestionType.Date;
}

export interface IOption {
    _id?: string;
    option_id: string;
    answer_text: string;
    order?: number;
    linked_object_id?: string;
    _type?: string;
}

export interface IOptionableQuestion extends IQuestion {
    text: string;
    options: IOption[];
    description?: string;
}

export interface IDropdownQuestion extends IOptionableQuestion {
    question_type: QuestionType.Dropdown;
    placeholder?: string;
}

export interface ISingleChoiceQuestion extends IOptionableQuestion {
    question_type: QuestionType.SingleChoice;
}

export interface IMultiChoiceQuestion extends IOptionableQuestion {
    question_type: QuestionType.MultiChoice;
}

export type TQuestion =
    | ITextQuestion
    | IDropdownQuestion
    | ISingleChoiceQuestion
    | IMultiChoiceQuestion
    | IDateQuestion
    | IUploadQuestion;

export type TOptionableQuestion =
    | IDropdownQuestion
    | ISingleChoiceQuestion
    | IMultiChoiceQuestion;

type TRootLevelChildrenFunction = ({
    hoverProps
}: {
    hoverProps: IHoverProps;
}) => ReactNode | ReactNode[];

export interface IRootLevelBlockProps {
    isFirst: boolean;
    isLast: boolean;
    selected: boolean;
    isSelectable?: boolean;
    isThereASelectedBlock: boolean;
    iAmTheParentOfTheSelectedBlock: boolean;
    previewMode?: boolean;
    isChargeRootLevelBlock?: boolean;
    block: ContainerableBlock;
    children?: ReactNode | ReactNode[] | TRootLevelChildrenFunction;
    beforeDeleteWarning?: ReactNode;
    isDeleteDisabled?: boolean;
    deleteDisabledTooltip?: string | JSX.Element;
    dragDisabledTooltip?: string | JSX.Element;
    onClick?: () => void;
}

export interface IHoverProps {
    isRootLevelBlockHovered: boolean;
    isRootLevelBlockHoveredExact: boolean;
    onBlockEnter: (
        ev: React.MouseEvent<HTMLElement, MouseEvent>,
        id: string
    ) => void;
    onBlockLeave: (id: string) => void;
}

export interface IBlockProps {
    block: TBlock;
    displayRootParentBlock?: ContainerableBlock;
    selected?: boolean;
    hidePlus?: boolean;
    isSelectable?: boolean;
    isDeleteDisabled?: boolean;
    deleteTooltip?: string | JSX.Element;
    deleteDisabledTooltip?: string | JSX.Element;
    dragDisabledTooltip?: string | JSX.Element;
    hoverProps?: IHoverProps;
    // TODO: remove specific hover props and use hoverProps interface
    hoveredRootLevelBlockId?: string;
    onBlockEnter?: (
        ev: React.MouseEvent<HTMLElement, MouseEvent>,
        id: string
    ) => void;
    onBlockLeave?: (id: string) => void;
    getIsHovered?: (id: string) => boolean;
    isHovered?: boolean;
    hasZIndex?: boolean;
    previewMode?: boolean;
    beforeDeleteWarning?: string;
    isDeletable?: boolean;
    isRootSelected?: boolean;
    isRootFocused?: boolean;
    isFirst?: boolean;
    additionalProps?: any;
    onDeleteOverride?: () => void;
}

export type TPreviewTabId = 'Desktop' | 'Mobile';

export enum PlusLocation {
    Top = 'top',
    Bottom = 'bottom'
}

export enum SmartFieldType {
    Inline = 'inline',
    Block = 'block'
}

export enum SmartBlockType {
    Payments = 'payments_recap',
    Services = 'services_recap'
}

export enum FlowStages {
    'draft' = 0,
    'sent' = 1,
    'viewed' = 2,
    'in_progress' = 3,
    'completed' = 4
}

export type TResponseBlock =
    | QuestionBlock
    | ContractBlock
    | InvoiceBlock
    | ServiceSelectionBlock
    | ChargeBlock
    | ScheduleContainerBlock;

export type TResponsesBlockTypes =
    | 'question_block'
    | 'contract_block'
    | 'invoice_container_block'
    | 'charge_container_block'
    | 'service_selection_block'
    | 'schedule_container_block';

export enum ResponsesActionBlocks {
    Invoice = 'invoice_container_block',
    Contract = 'contract_block',
    ServiceSelection = 'service_selection_block',
    ScheduleContainer = 'schedule_container_block'
}

export enum PaymentModals {
    ViewPaymentReceipt = 'ViewPaymentReceipt',
    MarkAsPaid = 'MarkAsPaid',
    MarkAsRefund = 'MarkAsRefund',
    UndoMarkAsPaid = 'UndoMarkAsPaid',
    RefundCustomAmount = 'RefundCustomAmount',
    ViewPaymentDetails = 'ViewPaymentDetails'
}

export interface IPreviewContext {
    forceMobile: boolean;
    isExpiryPreview: boolean;
    expiryPreviewMessage: string;
    isPreviewContractSigned: boolean;
    isPreviewServiceSelected: boolean;
    goToWorkspace?: () => void;
    goToTemplateGallery?: () => void;
    previewSource?: TPreviewSource;
    isPreviewLastPageModalOpen?: boolean;
    exitPreview?: () => void;
}

export interface IIsolatedBlockEditorState {
    blocks?: TBlock[];
    selectedBlockId?: string;
    additionalProps?: any;
    prevSelectedBlockId?: string;
}

export enum ServiceItemScope {
    Invoice = 'Invoice',
    ServiceSelection = 'ServiceSelection',
    ServiceSelectionSubItemsModal = 'ServiceSelectionSubItemsModal'
}

export enum RegainControlModalReasons {
    localFlowBehindServer = 'localFlowBehindServer',
    flowNotInControl = 'flowNotInControl',
    tempAccessTriedGetControlFromVendor = 'tempAccessTriedGetControlFromVendor'
}

export type TServiceItemBlock =
    | InvoiceServiceItemBlock
    | ServiceSelectionItemBlock;

export enum ServiceItemBlockTypes {
    ServiceSelection = 'service_selection_item_block',
    InvoiceService = 'invoice_service_item_block'
}

export interface BaseBlockType {
    comingSoon?: boolean; // todo: remove this and it's references when service and schedule block are ready in the future
    compositionContentType?: CompositionContentType;
    compositionType?: CompositionType;
    compositionLayoutType?: CompositionLayoutType;
    disabledTooltip?: string;
    icon: ({ svgProps, ...props }: any) => JSX.Element;
    isDisabled?: boolean;
    isNew?: boolean;
    isNewLove?: boolean;
    name: string;
    type: TBlockType;
    additionalAttributes?: any;
}

export interface ICreationLimitDialogDetails {
    approveCTA: { text: string; callback?: Function };
    description: string | ReactElement;
    iconComponent?: JSX.Element;
    isOpen?: boolean;
    rejectCTA: { text: string; callback?: Function };
    title: string;
}

export type TOpenSendTestModalSources =
    | 'preview_top_nav'
    | 'top_nav'
    | 'builder_top_nav_menu';

export interface ServiceToPackageProps {
    block: ServiceItemBlock;
    type: TBlockType;
}

export type AddItemToPackageProps = {
    block: ServiceItemBlock;
};

export type BlockInputValidationModel = {
    [key in PaymentScheduleBlockErrors]?: boolean;
};

export type TPreviewSource =
    | 'builder'
    | 'setup_guide'
    | 'scheduler_intent_onboarding'
    | 'lead_form_share_modal'
    | 'lead_form_onboarding'
    | 'invoice_intent_onboarding_preview_test'
    | 'flow_wizard'
    | 'reveal_setup';

export interface IConfigureModalProps
    extends Pick<IModal, 'scrollToRef' | 'isChildModal'> {
    CTAText?: string;
    defaultDraftInvoiceToggleOn?: boolean;
}

export type TBuilderQueryParams = {
    page?: 'contract' | 'scheduler' | 'invoice';
    isFromOnboarding?: 'true';
    openConfigureModal?: 'true';
    configure?: 'true';
    new?: 'true';
    blockToSelect?: TBlockType;
    scrollTo?: string;
    backToPathname?: string;
};

export type TScheduleTriggerType = 'immediately' | 'once_invoice_paid';

export type TScheduleBlockModes =
    | 'edit'
    | 'config_disabled'
    | 'config_deleted'
    | 'scheduled';
